/**
 * Pricing Page - Clinafy Institutional Website
 *
 * Professional pricing page with all plans, comparison table,
 * and detailed feature breakdown.
 */

import React, { useState } from 'react';
import { useSEO, SEO_CONFIG } from '../../hooks/useSEO';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Tooltip,
  Divider,
  alpha,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

// Icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import StarIcon from '@mui/icons-material/Star';
import VerifiedIcon from '@mui/icons-material/Verified';
import PersonIcon from '@mui/icons-material/Person';
import GroupsIcon from '@mui/icons-material/Groups';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import DiamondIcon from '@mui/icons-material/Diamond';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Components
import Navbar from '../../components/landing/Navbar';
import Footer from '../../components/landing/Footer';
import { landingColors, landingGradients, landingShadows } from '../../theme/landingTheme';

// Plan config
import {
  PLAN_DEFINITIONS,
  PlanId,
  PlanFeatureFlags,
  formatPrice,
} from '../../config/plans';

// Analytics
import {
  trackCTAClick,
  trackTrialStart,
  buildSignupUrl,
} from '../../utils/analytics';

// ============================================================================
// Data
// ============================================================================

const pricingFaqs = [
  {
    question: 'Posso mudar de plano depois?',
    answer: 'Sim! Você pode fazer upgrade ou downgrade a qualquer momento. O valor é calculado proporcionalmente ao tempo restante do seu ciclo de cobrança.',
  },
  {
    question: 'Aceita cartão de crédito e PIX?',
    answer: 'Sim! Aceitamos cartões de crédito de todas as bandeiras e PIX. Para cartão, o pagamento é processado pelo MercadoPago com total segurança.',
  },
  {
    question: 'Tem desconto para pagamento anual?',
    answer: 'Sim! Oferecemos 20% de desconto para pagamentos anuais. Entre em contato com nosso time comercial para saber mais.',
  },
  {
    question: 'O que acontece com meus dados se eu cancelar?',
    answer: 'Seus dados ficam disponíveis para exportação por 30 dias após o cancelamento. Depois disso, são permanentemente apagados conforme a LGPD.',
  },
  {
    question: 'Posso testar antes de assinar?',
    answer: 'Claro! Oferecemos 30 dias de teste gratuito. Não pedimos cartão de crédito para começar.',
  },
  {
    question: 'Tem taxa de adesão ou multa de cancelamento?',
    answer: 'Não! Sem taxas escondidas, sem multas. Você paga apenas a mensalidade do plano escolhido e pode cancelar quando quiser.',
  },
];

// Order for display - showing 4 main paid plans
const displayPlanOrder: PlanId[] = ['individual', 'small_clinic', 'clinic_pro', 'enterprise'];

// Features for comparison table with tooltips
const comparisonFeatures: Array<{
  name: string;
  key?: keyof PlanFeatureFlags;
  tooltip?: string;
  isCore?: boolean;
}> = [
  // Core Features
  { name: 'Prontuário Eletrônico', isCore: true, tooltip: 'Registro completo de sessões e evolução do paciente' },
  { name: 'Agenda Integrada', key: 'calendar', tooltip: 'Calendário com agendamentos e lembretes' },
  { name: 'Relatórios Básicos', key: 'basicReports', tooltip: 'Relatórios de atendimentos e financeiros' },

  // Patient Experience
  { name: 'Agendamento Online', key: 'onlineBooking', tooltip: 'Página pública para pacientes solicitarem horários' },
  { name: 'Portal do Paciente', key: 'patientPortal', tooltip: 'Pacientes acessam consultas, documentos e confirmam presença' },

  // Documents & Assessments
  { name: 'Modelos de Documentos', key: 'documentTemplates', tooltip: 'Templates para recibos, declarações e relatórios clínicos' },
  { name: 'Geração de Documentos', key: 'documentGeneration', tooltip: 'Geração automática de recibos e declarações' },
  { name: 'Avaliações Psicológicas', key: 'assessments', tooltip: 'Instrumentos padronizados com pontuação automática' },

  // Advanced Scheduling
  { name: 'Fila de Espera', key: 'waitingList', tooltip: 'Gestão de lista de espera com notificações automáticas' },

  // Financial & Reports
  { name: 'Módulo Financeiro', key: 'financial', tooltip: 'Controle de pagamentos, recibos e inadimplência' },
  { name: 'Relatórios Avançados', key: 'advancedReports', tooltip: 'Análises detalhadas e exportação' },
  { name: 'Dashboard Avançado', key: 'advancedDashboard', tooltip: 'Métricas e KPIs da clínica' },

  // Progress & Analytics
  { name: 'Dashboard de Progresso', key: 'progressDashboard', tooltip: 'Acompanhamento visual da evolução do paciente' },

  // Video & Communication
  { name: 'Teleconsulta (Vídeo)', key: 'videoCall', tooltip: 'Videochamada integrada via Jitsi' },
  { name: 'Notificações Email', key: 'emailNotifications', tooltip: 'Lembretes automáticos por email' },
  { name: 'Notificações WhatsApp', key: 'whatsappNotifications', tooltip: 'Lembretes automáticos via WhatsApp' },

  // AI & Voice Features
  { name: 'Voz para Texto', key: 'voiceToText', tooltip: 'Transcrição de notas por voz em tempo real' },
  { name: 'Transcrição IA', key: 'aiTranscription', tooltip: 'Transcrição automática de sessões com Deepgram' },
  { name: 'Insights com IA', key: 'aiInsights', tooltip: 'Sugestões baseadas no histórico do paciente' },

  // Marketing & Growth
  { name: 'Programa de Indicação', key: 'referralProgram', tooltip: 'Sistema de referral com recompensas' },

  // Premium Features
  { name: 'Marca Personalizada', key: 'customBranding', tooltip: 'Sua logo e cores na plataforma' },
  { name: 'Acesso à API', key: 'apiAccess', tooltip: 'Integração com outros sistemas via API' },
  { name: 'Suporte Prioritário', key: 'prioritySupport', tooltip: 'Atendimento em até 2 horas' },
  { name: 'Suporte Dedicado 24/7', key: 'dedicatedSupport', tooltip: 'Gerente de conta exclusivo' },
];

// Plan icons
const planIcons: Record<PlanId, React.ReactNode> = {
  trial: <PersonIcon />,
  individual: <PersonIcon />,
  small_clinic: <GroupsIcon />,
  clinic_pro: <WorkspacePremiumIcon />,
  enterprise: <DiamondIcon />,
};

// ============================================================================
// Component
// ============================================================================

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const [expandedFaq, setExpandedFaq] = useState<string | false>(false);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');

  // SEO - Dynamic meta tags
  useSEO(SEO_CONFIG.planos);

  const handleFaqChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedFaq(isExpanded ? panel : false);
  };

  const getFeatureValue = (planId: PlanId, feature: typeof comparisonFeatures[0]): boolean | string => {
    const plan = PLAN_DEFINITIONS[planId];
    if (!plan) return false;

    // Core features always available
    if (feature.isCore) return true;

    if (feature.key) {
      // Check AI transcription limits
      if (feature.key === 'aiTranscription' && plan.features.aiTranscription) {
        const limit = plan.limits.aiTranscriptionsPerMonth;
        if (limit === -1) return 'Ilimitado';
        if (limit) return `${limit}/mês`;
        return true;
      }
      return plan.features[feature.key];
    }

    return false;
  };

  const renderFeatureValue = (value: boolean | string) => {
    if (value === true) {
      return <CheckCircleIcon sx={{ color: landingColors.accent.teal, fontSize: 24 }} />;
    }
    if (value === false) {
      return <CancelIcon sx={{ color: landingColors.neutral.gray300, fontSize: 24 }} />;
    }
    return (
      <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: landingColors.primary.main }}>
        {value}
      </Typography>
    );
  };

  return (
    <Box sx={{ bgcolor: landingColors.background.default }}>
      <Navbar />

      <Box component="main" id="main-content">
      {/* ================================================================== */}
      {/* HERO SECTION */}
      {/* ================================================================== */}
      <Box
        sx={{
          background: landingGradients.hero,
          pt: { xs: 8, md: 10 },
          pb: { xs: 10, md: 14 },
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            align="center"
            sx={{
              color: landingColors.neutral.white,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 700,
              mb: 2,
            }}
          >
            Planos que cabem no seu bolso
          </Typography>
          <Typography
            align="center"
            sx={{
              color: alpha(landingColors.neutral.white, 0.9),
              fontSize: { xs: '1.1rem', md: '1.25rem' },
              maxWidth: 700,
              mx: 'auto',
              mb: 3,
            }}
          >
            Escolha o plano ideal para seu momento. Sem surpresas, sem taxas escondidas.
          </Typography>
          <Box sx={{ textAlign: 'center' }}>
            <Chip
              icon={<VerifiedIcon sx={{ fontSize: 18 }} />}
              label="30 dias grátis para testar qualquer plano"
              sx={{
                bgcolor: alpha(landingColors.neutral.white, 0.2),
                color: landingColors.neutral.white,
                fontWeight: 500,
                py: 2.5,
                px: 1,
                fontSize: '0.95rem',
                '& .MuiChip-icon': { color: landingColors.accent.yellow },
              }}
            />
          </Box>
        </Container>
      </Box>

      {/* ================================================================== */}
      {/* PRICING CARDS */}
      {/* ================================================================== */}
      <Box sx={{ py: { xs: 6, md: 8 }, mt: { xs: -6, md: -10 } }}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 4 }}>
            <Typography variant="body1" color={billingPeriod === 'monthly' ? 'primary' : 'text.secondary'} fontWeight={billingPeriod === 'monthly' ? 'bold' : 'normal'}>
              Mensal
            </Typography>
            <Switch
              checked={billingPeriod === 'annual'}
              onChange={() => setBillingPeriod(prev => prev === 'monthly' ? 'annual' : 'monthly')}
              color="primary"
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1" color={billingPeriod === 'annual' ? 'primary' : 'text.secondary'} fontWeight={billingPeriod === 'annual' ? 'bold' : 'normal'}>
                Anual
              </Typography>
              <Chip label="Economize 20%" size="small" color="success" sx={{ fontWeight: 'bold' }} />
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3, fontStyle: 'italic' }}>
            Preço de lançamento — garantimos este valor por 12 meses após assinatura
          </Typography>
          <Grid container spacing={3} justifyContent="center">
            {displayPlanOrder.map((planId) => {
              const plan = PLAN_DEFINITIONS[planId];
              if (!plan) return null;

              const isPopular = plan.isPopular;

              return (
                <Grid key={planId} size={{ xs: 12, sm: 6, lg: 3 }}>
                  <Card
                    sx={{
                      height: '100%',
                      borderRadius: 4,
                      border: isPopular
                        ? `3px solid ${landingColors.primary.main}`
                        : `1px solid ${landingColors.neutral.gray200}`,
                      boxShadow: isPopular ? landingShadows.xl : landingShadows.card,
                      position: 'relative',
                      overflow: 'visible',
                      transform: isPopular ? { lg: 'scale(1.05)' } : 'none',
                      zIndex: isPopular ? 2 : 1,
                      bgcolor: '#FFFFFF',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: isPopular
                          ? { xs: 'translateY(-8px)', lg: 'scale(1.05) translateY(-8px)' }
                          : 'translateY(-8px)',
                        boxShadow: landingShadows.xl,
                      },
                    }}
                  >
                    {/* Popular Badge */}
                    {isPopular && (
                      <Chip
                        icon={<StarIcon sx={{ fontSize: 16 }} />}
                        label="Mais Popular"
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: -14,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          bgcolor: landingColors.primary.main,
                          color: landingColors.neutral.white,
                          fontWeight: 700,
                          fontSize: '0.8rem',
                          py: 2,
                          '& .MuiChip-icon': { color: landingColors.accent.yellow },
                        }}
                      />
                    )}

                    <CardContent sx={{ p: 4 }}>
                      {/* Plan Icon & Name */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2,
                            bgcolor: alpha(plan.colors.badge, 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: plan.colors.badge,
                          }}
                        >
                          {planIcons[planId]}
                        </Box>
                        <Box>
                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: 700,
                              color: landingColors.neutral.gray900,
                              lineHeight: 1.2,
                            }}
                          >
                            {plan.displayName}
                          </Typography>
                          <Typography sx={{ color: landingColors.neutral.gray500, fontSize: '0.85rem' }}>
                            {plan.description}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Price */}
                      {(() => {
                        const displayPrice = billingPeriod === 'annual'
                          ? Math.round(plan.price * 0.8 * 100) / 100
                          : plan.price;
                        return (
                          <Box sx={{ mb: 3, mt: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                              <Typography sx={{ fontSize: '1rem', fontWeight: 500, color: landingColors.neutral.gray500 }}>
                                R$
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: '3.5rem',
                                  fontWeight: 800,
                                  color: plan.colors.badge,
                                  lineHeight: 1,
                                  letterSpacing: '-0.02em',
                                }}
                              >
                                {Math.floor(displayPrice)}
                              </Typography>
                              <Typography sx={{ fontSize: '1.5rem', fontWeight: 600, color: plan.colors.badge, mb: 1 }}>
                                ,{(displayPrice % 1).toFixed(2).substring(2)}
                              </Typography>
                            </Box>
                            <Typography sx={{ color: landingColors.neutral.gray500, fontSize: '0.9rem' }}>
                              /mês
                            </Typography>
                            {billingPeriod === 'annual' && (
                              <>
                                <Typography sx={{ color: landingColors.accent.teal, fontSize: '0.8rem', fontWeight: 500 }}>
                                  cobrado anualmente
                                </Typography>
                                <Typography sx={{ color: landingColors.neutral.gray500, fontSize: '0.8rem', fontWeight: 500, mt: 0.5 }}>
                                  Total: R$ {(Math.round(plan.price * 0.8 * 12 * 100) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/ano
                                </Typography>
                              </>
                            )}
                          </Box>
                        );
                      })()}

                      {/* CTA Button */}
                      <Button
                        fullWidth
                        variant={isPopular ? 'contained' : 'outlined'}
                        size="large"
                        onClick={() => {
                          trackCTAClick('plan_select', 'pricing_card', '/signup');
                          navigate(buildSignupUrl('/signup', { plan: plan.id }));
                        }}
                        sx={{
                          py: 1.5,
                          fontWeight: 700,
                          fontSize: '1rem',
                          borderRadius: 2,
                          mb: 3,
                          ...(isPopular
                            ? {
                                bgcolor: landingColors.primary.main,
                                boxShadow: landingShadows.button,
                                '&:hover': {
                                  bgcolor: landingColors.primary.dark,
                                  boxShadow: landingShadows.buttonHover,
                                },
                              }
                            : {
                                borderColor: plan.colors.badge,
                                borderWidth: 2,
                                color: plan.colors.badge,
                                '&:hover': {
                                  borderColor: plan.colors.badge,
                                  borderWidth: 2,
                                  bgcolor: alpha(plan.colors.badge, 0.05),
                                },
                              }),
                        }}
                      >
                        Começar Agora
                      </Button>

                      <Divider sx={{ mb: 3 }} />

                      {/* Key Features */}
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: landingColors.neutral.gray800,
                          mb: 2,
                          fontSize: '0.9rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                        }}
                      >
                        Inclui:
                      </Typography>

                      <Stack spacing={1.5}>
                        {/* Limits */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <CheckCircleIcon sx={{ fontSize: 20, color: landingColors.accent.teal }} />
                          <Typography sx={{ fontSize: '0.9rem', color: landingColors.neutral.gray700 }}>
                            {plan.limits.maxUsers === -1
                              ? 'Usuários ilimitados'
                              : plan.limits.maxUsers === 1
                              ? '1 usuário'
                              : `Até ${plan.limits.maxUsers} usuários`}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <CheckCircleIcon sx={{ fontSize: 20, color: landingColors.accent.teal }} />
                          <Typography sx={{ fontSize: '0.9rem', color: landingColors.neutral.gray700 }}>
                            {plan.limits.maxClients === -1
                              ? 'Pacientes ilimitados'
                              : `Até ${plan.limits.maxClients} pacientes`}
                          </Typography>
                        </Box>

                        {/* Core Features */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <CheckCircleIcon sx={{ fontSize: 20, color: landingColors.accent.teal }} />
                          <Typography sx={{ fontSize: '0.9rem', color: landingColors.neutral.gray700 }}>
                            Prontuário eletrônico
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <CheckCircleIcon sx={{ fontSize: 20, color: landingColors.accent.teal }} />
                          <Typography sx={{ fontSize: '0.9rem', color: landingColors.neutral.gray700 }}>
                            Agenda integrada
                          </Typography>
                        </Box>

                        {plan.features.financial && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <CheckCircleIcon sx={{ fontSize: 20, color: landingColors.accent.teal }} />
                            <Typography sx={{ fontSize: '0.9rem', color: landingColors.neutral.gray700 }}>
                              Módulo financeiro
                            </Typography>
                          </Box>
                        )}

                        {plan.features.videoCall && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <CheckCircleIcon sx={{ fontSize: 20, color: landingColors.accent.teal }} />
                            <Typography sx={{ fontSize: '0.9rem', color: landingColors.neutral.gray700 }}>
                              Teleconsulta (vídeo)
                            </Typography>
                          </Box>
                        )}

                        {plan.features.whatsappNotifications && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <CheckCircleIcon sx={{ fontSize: 20, color: landingColors.accent.teal }} />
                            <Typography sx={{ fontSize: '0.9rem', color: landingColors.neutral.gray700 }}>
                              Lembretes WhatsApp
                            </Typography>
                          </Box>
                        )}

                        {plan.features.aiTranscription && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <CheckCircleIcon sx={{ fontSize: 20, color: landingColors.accent.teal }} />
                            <Typography sx={{ fontSize: '0.9rem', color: landingColors.neutral.gray700 }}>
                              Transcrição IA
                              {plan.limits.aiTranscriptionsPerMonth !== -1 && plan.limits.aiTranscriptionsPerMonth
                                ? ` (${plan.limits.aiTranscriptionsPerMonth}/mês)`
                                : ' (ilimitada)'}
                            </Typography>
                          </Box>
                        )}

                        {plan.features.prioritySupport && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <CheckCircleIcon sx={{ fontSize: 20, color: landingColors.accent.teal }} />
                            <Typography sx={{ fontSize: '0.9rem', color: landingColors.neutral.gray700 }}>
                              Suporte prioritário
                            </Typography>
                          </Box>
                        )}

                        {plan.features.dedicatedSupport && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <CheckCircleIcon sx={{ fontSize: 20, color: landingColors.accent.teal }} />
                            <Typography sx={{ fontSize: '0.9rem', color: landingColors.neutral.gray700 }}>
                              Suporte dedicado 24/7
                            </Typography>
                          </Box>
                        )}

                        {plan.features.customBranding && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <CheckCircleIcon sx={{ fontSize: 20, color: landingColors.accent.teal }} />
                            <Typography sx={{ fontSize: '0.9rem', color: landingColors.neutral.gray700 }}>
                              Marca personalizada
                            </Typography>
                          </Box>
                        )}

                        {plan.features.apiAccess && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <CheckCircleIcon sx={{ fontSize: 20, color: landingColors.accent.teal }} />
                            <Typography sx={{ fontSize: '0.9rem', color: landingColors.neutral.gray700 }}>
                              Acesso a API
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      {/* ================================================================== */}
      {/* GUARANTEE SECTION */}
      {/* ================================================================== */}
      <Box sx={{ py: 6 }}>
        <Container maxWidth="lg">
          <Card
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: 4,
              background: `linear-gradient(135deg, ${alpha(landingColors.accent.teal, 0.05)} 0%, ${alpha(landingColors.primary.main, 0.05)} 100%)`,
              border: `2px solid ${landingColors.accent.teal}`,
            }}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid size={{ xs: 12, md: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', md: 'flex-start' }, gap: 2 }}>
                  <VerifiedIcon sx={{ fontSize: 48, color: landingColors.accent.teal }} />
                  <Typography variant="h4" sx={{ fontWeight: 700, color: landingColors.neutral.gray900 }}>
                    Garantia Clinafy
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 8 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon sx={{ color: landingColors.accent.teal }} />
                      <Typography sx={{ fontWeight: 600, color: landingColors.neutral.gray700 }}>
                        30 dias grátis
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon sx={{ color: landingColors.accent.teal }} />
                      <Typography sx={{ fontWeight: 600, color: landingColors.neutral.gray700 }}>
                        Cancele quando quiser
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon sx={{ color: landingColors.accent.teal }} />
                      <Typography sx={{ fontWeight: 600, color: landingColors.neutral.gray700 }}>
                        Migração gratuita
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Card>
        </Container>
      </Box>

      {/* ================================================================== */}
      {/* COMPARISON TABLE */}
      {/* ================================================================== */}
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: landingColors.background.alt }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            align="center"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '2.5rem' },
              color: landingColors.neutral.gray900,
              mb: 2,
            }}
          >
            Compare todos os recursos
          </Typography>
          <Typography
            align="center"
            sx={{
              color: landingColors.neutral.gray600,
              fontSize: '1.1rem',
              mb: 6,
              maxWidth: 600,
              mx: 'auto',
            }}
          >
            Veja exatamente o que cada plano oferece
          </Typography>

          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 3,
              boxShadow: landingShadows.lg,
              overflow: 'auto',
              bgcolor: '#FFFFFF',
              // No mobile a tabela larga vira cards (bloco abaixo)
              display: { xs: 'none', md: 'block' },
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      minWidth: 220,
                      bgcolor: landingColors.neutral.gray50,
                      color: landingColors.neutral.gray900,
                    }}
                  >
                    Recursos
                  </TableCell>
                  {displayPlanOrder.map((planId) => {
                    const plan = PLAN_DEFINITIONS[planId];
                    return (
                      <TableCell
                        key={planId}
                        align="center"
                        sx={{
                          fontWeight: 700,
                          bgcolor: plan.isPopular
                            ? alpha(landingColors.primary.main, 0.08)
                            : landingColors.neutral.gray50,
                          minWidth: 130,
                        }}
                      >
                        <Typography sx={{ fontWeight: 700, color: plan.colors.badge, fontSize: '1rem' }}>
                          {plan.displayName}
                        </Typography>
                        <Typography sx={{ color: landingColors.neutral.gray500, fontSize: '0.85rem' }}>
                          {formatPrice(plan.price)}/mês
                        </Typography>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Limits Rows */}
                <TableRow sx={{ bgcolor: alpha(landingColors.primary.main, 0.02) }}>
                  <TableCell sx={{ fontWeight: 600, color: landingColors.neutral.gray800 }}>
                    Usuários
                  </TableCell>
                  {displayPlanOrder.map((planId) => {
                    const plan = PLAN_DEFINITIONS[planId];
                    return (
                      <TableCell
                        key={planId}
                        align="center"
                        sx={{ bgcolor: plan.isPopular ? alpha(landingColors.primary.main, 0.04) : 'transparent' }}
                      >
                        <Typography sx={{ fontWeight: 600, color: landingColors.primary.main }}>
                          {plan.limits.maxUsers === -1 ? 'Ilimitado' : plan.limits.maxUsers}
                        </Typography>
                      </TableCell>
                    );
                  })}
                </TableRow>
                <TableRow sx={{ bgcolor: alpha(landingColors.primary.main, 0.02) }}>
                  <TableCell sx={{ fontWeight: 600, color: landingColors.neutral.gray800 }}>
                    Pacientes
                  </TableCell>
                  {displayPlanOrder.map((planId) => {
                    const plan = PLAN_DEFINITIONS[planId];
                    return (
                      <TableCell
                        key={planId}
                        align="center"
                        sx={{ bgcolor: plan.isPopular ? alpha(landingColors.primary.main, 0.04) : 'transparent' }}
                      >
                        <Typography sx={{ fontWeight: 600, color: landingColors.primary.main }}>
                          {plan.limits.maxClients === -1 ? 'Ilimitado' : plan.limits.maxClients}
                        </Typography>
                      </TableCell>
                    );
                  })}
                </TableRow>

                {/* Feature Rows */}
                {comparisonFeatures.map((feature) => (
                  <TableRow key={feature.name} sx={{ '&:hover': { bgcolor: landingColors.neutral.gray50 } }}>
                    <TableCell sx={{ color: landingColors.neutral.gray700 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {feature.name}
                        {feature.tooltip && (
                          <Tooltip title={feature.tooltip} arrow>
                            <InfoOutlinedIcon sx={{ fontSize: 16, color: landingColors.neutral.gray400, cursor: 'help' }} />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    {displayPlanOrder.map((planId) => {
                      const plan = PLAN_DEFINITIONS[planId];
                      const value = getFeatureValue(planId, feature);
                      return (
                        <TableCell
                          key={planId}
                          align="center"
                          sx={{ bgcolor: plan.isPopular ? alpha(landingColors.primary.main, 0.02) : 'transparent' }}
                        >
                          {renderFeatureValue(value)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Versão mobile: um card por plano com todos os recursos empilhados
              (a tabela acima fica ilegível no celular). */}
          <Stack spacing={2.5} sx={{ display: { xs: 'flex', md: 'none' } }}>
            {displayPlanOrder.map((planId) => {
              const plan = PLAN_DEFINITIONS[planId];
              const rows: Array<{ label: string; node: React.ReactNode }> = [
                {
                  label: 'Usuários',
                  node: (
                    <Typography sx={{ fontWeight: 600, color: landingColors.primary.main }}>
                      {plan.limits.maxUsers === -1 ? 'Ilimitado' : plan.limits.maxUsers}
                    </Typography>
                  ),
                },
                {
                  label: 'Pacientes',
                  node: (
                    <Typography sx={{ fontWeight: 600, color: landingColors.primary.main }}>
                      {plan.limits.maxClients === -1 ? 'Ilimitado' : plan.limits.maxClients}
                    </Typography>
                  ),
                },
                ...comparisonFeatures.map((feature) => ({
                  label: feature.name,
                  node: renderFeatureValue(getFeatureValue(planId, feature)),
                })),
              ];
              return (
                <Card
                  key={planId}
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    borderColor: plan.isPopular ? landingColors.primary.main : landingColors.neutral.gray200,
                    borderWidth: plan.isPopular ? 2 : 1,
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1.5 }}>
                      <Typography sx={{ fontWeight: 700, color: plan.colors.badge, fontSize: '1.1rem' }}>
                        {plan.displayName}
                      </Typography>
                      <Typography sx={{ color: landingColors.neutral.gray600, fontSize: '0.9rem', fontWeight: 600 }}>
                        {formatPrice(plan.price)}/mês
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 1 }} />
                    {rows.map((r, i) => (
                      <Box
                        key={i}
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, py: 0.6 }}
                      >
                        <Typography sx={{ color: landingColors.neutral.gray700, fontSize: '0.9rem' }}>
                          {r.label}
                        </Typography>
                        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{r.node}</Box>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        </Container>
      </Box>

      {/* ================================================================== */}
      {/* FAQ SECTION */}
      {/* ================================================================== */}
      <Box sx={{ py: { xs: 8, md: 10 } }}>
        <Container maxWidth="md">
          <Typography
            variant="h2"
            align="center"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '2.5rem' },
              color: landingColors.neutral.gray900,
              mb: 6,
            }}
          >
            Dúvidas frequentes
          </Typography>

          {pricingFaqs.map((faq, index) => (
            <Accordion
              key={index}
              expanded={expandedFaq === `faq-${index}`}
              onChange={handleFaqChange(`faq-${index}`)}
              sx={{
                mb: 2,
                bgcolor: '#FFFFFF',
                borderRadius: '16px !important',
                border: `1px solid ${landingColors.neutral.gray200}`,
                boxShadow: 'none',
                '&:before': { display: 'none' },
                '&.Mui-expanded': { boxShadow: landingShadows.card },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 3, py: 1 }}>
                <Typography sx={{ fontWeight: 600, color: landingColors.neutral.gray900, fontSize: '1.05rem' }}>
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 3, pb: 3 }}>
                <Typography sx={{ color: landingColors.neutral.gray600, lineHeight: 1.7 }}>
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Container>
      </Box>

      {/* ================================================================== */}
      {/* FINAL CTA */}
      {/* ================================================================== */}
      <Box sx={{ background: landingGradients.heroDark, py: { xs: 8, md: 10 } }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography
            variant="h2"
            sx={{
              color: landingColors.neutral.white,
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '2.5rem' },
              mb: 2,
            }}
          >
            Comece hoje mesmo
          </Typography>
          <Typography sx={{ color: alpha(landingColors.neutral.white, 0.85), fontSize: '1.15rem', mb: 4 }}>
            30 dias grátis. Sem cartão de crédito. Sem compromisso.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => {
              trackTrialStart('pricing_final');
              navigate(buildSignupUrl('/signup', { trial: 'true' }));
            }}
            sx={{
              bgcolor: landingColors.neutral.white,
              color: landingColors.primary.main,
              fontWeight: 700,
              fontSize: '1.1rem',
              px: 6,
              py: 2,
              borderRadius: 2,
              boxShadow: landingShadows.button,
              '&:hover': {
                bgcolor: landingColors.neutral.gray100,
                boxShadow: landingShadows.buttonHover,
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            Começar Teste Gratuito
          </Button>
        </Container>
      </Box>

      </Box>

      <Footer />
    </Box>
  );
};

export default PricingPage;
